'use client';

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { CART_STORAGE_VERSION, migrateCartStorage, type CartStorage } from '../lib/cart-persist.utils';
import type { CartFormValues } from './cart-context';

export const CART_STORAGE_KEY = 'cart';

// Transient flags — must not survive reload (hydration gate / post-order redirect).
const CART_PERSIST_EXCLUDE = ['_isHydrated', '_isOrderCreated'] as const;

/**
 * Persist cart form to localStorage without re-rendering the host.
 * Uses RHF `subscribe({ values })` instead of `watch()` (which sets watchAll
 * and re-renders useForm's component on every keystroke).
 */
export const useCartFormPersist = (formMethods: UseFormReturn<CartFormValues>) => {
    const didRestoreRef = useRef(false);

    const onDataRestored = useCallback(() => {
        formMethods.setValue('_isHydrated', true);
    }, [formMethods]);

    useLayoutEffect(() => {
        const stored = localStorage.getItem(CART_STORAGE_KEY);
        if (!stored) {
            onDataRestored();
            return;
        }
        // Drop corrupt payloads before the restore effect JSON.parses them — a throw
        // here would crash the whole app (CartProvider wraps the tree) and retry forever.
        try {
            JSON.parse(stored);
        } catch {
            localStorage.removeItem(CART_STORAGE_KEY);
            onDataRestored();
        }
    }, [onDataRestored]);

    useEffect(() => {
        if (!didRestoreRef.current) {
            didRestoreRef.current = true;
            const stored = localStorage.getItem(CART_STORAGE_KEY);
            if (stored) {
                try {
                    const parsed = JSON.parse(stored) as CartStorage;
                    const migrated = migrateCartStorage(parsed);
                    if (!migrated.ok) {
                        localStorage.removeItem(CART_STORAGE_KEY);
                    } else {
                        const { values, didChange } = migrated;
                        if (didChange) {
                            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(values));
                        }
                        const { _v, ...formValues } = values;
                        for (const key of Object.keys(formValues) as (keyof CartFormValues)[]) {
                            if ((CART_PERSIST_EXCLUDE as readonly string[]).includes(key)) {
                                continue;
                            }
                            formMethods.setValue(key, formValues[key] as CartFormValues[typeof key]);
                        }
                    }
                    onDataRestored();
                } catch {
                    localStorage.removeItem(CART_STORAGE_KEY);
                    onDataRestored();
                }
            }
        }

        return formMethods.subscribe({
            formState: { values: true },
            callback: ({ values }) => {
                const toStore: CartStorage = { ...values, _v: CART_STORAGE_VERSION };
                for (const key of CART_PERSIST_EXCLUDE) {
                    delete toStore[key];
                }
                if (Object.keys(toStore).length) {
                    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(toStore));
                }
            },
        });
    }, [formMethods, onDataRestored]);
};
